import { Address } from 'viem';

import { REVERSE_PROXY_IP } from 'src/global/config/common.config';
import { ProjectStageEnum } from 'src/global/enums/project.enum';
import {
  CreateProjectDTOType,
  DeepPartial,
  ProjectInputProductionSchema,
  ProjectInputSchema,
} from 'src/global/schemas/project.schema';
import { ProjectInputs } from 'src/global/types/project.type';
import { TProjectTheme } from 'src/global/types/theme.type';

export const convertFormDataToPayload = (
  formData: ProjectInputs,
  creator: Address,
  theme: TProjectTheme,
): CreateProjectDTOType => {
  // in draft mode, recipients is only default value, so we set it to empty array
  const isRecipientsEmpty = formData.recipients?.length === 1 && formData.recipients[0].name === 'LFG';

  const formatData: CreateProjectDTOType = {
    creator,
    name: formData.name,
    info: {
      ...formData.info,
      dns: {
        type: 'CNAME',
        value: REVERSE_PROXY_IP,
        host: formData?.info?.nodeSaleUrl,
      },
    },
    faqs: formData.faqs,
    node: {
      ...formData.node,
      asset: formData?.node?.asset?.[0] as string,
    },
    phases: isRecipientsEmpty ? [] : formData.phases,
    recipients: formData.recipients.filter(recipient => recipient.name && recipient.address),
    referral: {
      ...formData.referral,
      rewardLv: formData.referral.rewardLv.map(reward => reward.node),
    },
    kols: formData.kols,
    setting: formData.setting,
    tier: formData.tier,
    royalties: formData.royalties.filter(royalty => royalty.address && royalty.percent),
    theme,
    threshold: formData.threshold,
    nodeSystem: formData.nodeSystem,
  };
  return formatData;
};

export const convertFormDataToPayloadForProduction = (
  payload: CreateProjectDTOType,
): DeepPartial<CreateProjectDTOType> => {
  const formatData: DeepPartial<CreateProjectDTOType> = {
    creator: payload.creator,
    name: payload.name,
    info: {
      url: payload.info?.url,
      description: payload.info?.description,
      whitepaperUrl: payload.info?.whitepaperUrl,
      termOfService: payload.info?.termOfService,
      privacyPolicy: payload.info?.privacyPolicy,
      gAnalyticId: payload.info?.gAnalyticId,
      network: payload.info?.network,
    },
    node: {
      ...payload.node,
      asset: payload?.node?.asset,
    },
    faqs: payload.faqs,
    setting: {
      maxPurchaseWl: payload.setting?.maxPurchaseWl,
      maxPurchaseTx: payload.setting?.maxPurchaseTx,
      defaultToken: payload.setting?.defaultToken,
    },
    referral: {
      rewardDescription: payload.referral?.rewardDescription,
    },
    nodeSystem: {
      email: payload.nodeSystem?.email,
    },
    theme: payload.theme,
  };
  return formatData;
};

/**
 * Get the appropriate Zod schema based on project status
 * @param projectStatus - The current status of the project
 * @returns The appropriate Zod schema for validation
 */
export const getProjectValidationSchema = (projectStatus?: ProjectStageEnum) => {
  if (projectStatus === ProjectStageEnum.PRODUCTION) {
    return ProjectInputProductionSchema;
  }
  return ProjectInputSchema;
};
